import dayjs from "dayjs";

import WarningIcon from "@zenith/assets/icons/components/WarningIcon";
import { formatCurrency } from "@zenith/utils";
import {
  TRANSACTION_DATE_FORMAT,
  TRANSACTION_TIME_FORMAT,
} from "@zenith/data/constants";
import { memo, useState } from "react";
import { ButtonComponent, SpinnerComponent } from "@zenith/components";

/* eslint-disable-next-line */
export interface PaymentNotConfirmedProps {
  message?: string;
  date: Date;
  chargedAmount: number;
  handleGoToDashboard?: () => void;
  handleReconfirmPayment: () => Promise<void>;
}

const PaymentNotConfirmed = memo(function PaymentNotConfirmed(
  props: PaymentNotConfirmedProps
) {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isReconfirming, setIsReconfirming] = useState(false);

  const handleReconfirm = async () => {
    setErrorMessage(undefined);
    setIsReconfirming(true);
    try {
      await props.handleReconfirmPayment();
    } catch (error: any) {
      setErrorMessage(
        error.message || "Failed to reconfirm payment. Please try again."
      );
    } finally {
      setIsReconfirming(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-center rounded-2.5xl border border-neutral-200 p-8 md:w-120">
      <WarningIcon />
      <h1 className="mt-4 text-xl font-medium text-neutral-900">
        Payment Not Confirmed
      </h1>

      <div className="my-8 flex rounded-1.25l bg-yellow-100 p-4">
        <span>
          <WarningIcon height={24} width={24} />
        </span>
        <p className="ml-3 text-sm">
          {props.message ||
            "Unable to confirm payment. Please verify with customer that the transaction was successful and try again."}
        </p>
      </div>
      <div className="w-full">
        <div className="mb-2 flex justify-between">
          <p className="text-neutral-600">Date</p>
          <p>{dayjs(props.date).format(TRANSACTION_DATE_FORMAT)}</p>
        </div>
        <div className="mb-2 flex justify-between">
          <p className="text-neutral-600">Time</p>
          <p>{dayjs(props.date).format(TRANSACTION_TIME_FORMAT)}</p>
        </div>
        <hr className="my-4 border-t-1 border-neutral-200" />
        <div className="mb-2 mb-[230px] flex justify-between">
          <p className="text-neutral-600">Amount Charged</p>
          <p>{formatCurrency(props.chargedAmount)}</p>
        </div>
        <div className="flex justify-center space-x-4">
          {props.handleGoToDashboard && (
            <ButtonComponent
              label="Go to Dashboard"
              onClick={props.handleGoToDashboard}
              id="go-to-dahsboard-button"
              size="large"
              type="secondary"
              fullWidth
            />
          )}

          <ButtonComponent
            label="Reconfirm Payment"
            onClick={handleReconfirm}
            id="reconfirm-payment-button"
            size="large"
            type="primary"
            fullWidth
            disabled={isReconfirming}
            isLoading={isReconfirming}
          />
        </div>
      </div>
    </div>
  );
});

export default PaymentNotConfirmed;
